import { useEffect, useMemo, useState } from 'react';
/**
 * Client-side pagination for `StarjackTable`. Slices `items` into the current
 * page and resets to the first page whenever `items` changes (e.g. a new
 * search/filter result).
 */
export const usePagination = ({ items = [], itemsPerPage, resetKey, }) => {
    const [currentPage, setCurrentPage] = useState(0);
    const [currentItemsPerPage, setCurrentItemsPerPage] = useState(itemsPerPage || 10);
    const currentItems = useMemo(() => {
        return items?.length
            ? items.slice(currentPage * currentItemsPerPage, currentPage * currentItemsPerPage + currentItemsPerPage)
            : [];
    }, [currentPage, items, currentItemsPerPage]);
    // Reset to the first page only when the dataset changes. `currentPage` must
    // NOT be a dependency here — including it makes every page change re-run the
    // effect and snap back to page 0 (the original pagination bug).
    const hasResetKey = resetKey !== undefined;
    useEffect(() => {
        if (!hasResetKey)
            setCurrentPage(0);
    }, [items]);
    // Opt-in: reset only when the filter changes, not on every refetch.
    useEffect(() => {
        if (hasResetKey)
            setCurrentPage(0);
    }, [resetKey]);
    // Opt-in: keep the page on refetch, clamp it if the list got shorter.
    useEffect(() => {
        if (!hasResetKey)
            return;
        const lastPage = Math.max(0, Math.ceil(items.length / currentItemsPerPage) - 1);
        setCurrentPage((page) => Math.min(page, lastPage));
    }, [items, currentItemsPerPage]);
    return {
        currentItems,
        setNextPage: setCurrentPage,
        page: currentPage,
        setItemsPerPage: setCurrentItemsPerPage,
        itemsPerPage: currentItemsPerPage,
        countTotal: items.length,
    };
};
